import { ChangeEvent, FC, MouseEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Grid, Button, CircularProgress, Box } from '@mui/material'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { useDownloadExcel } from 'react-export-table-to-excel'
import { ICustomer } from 'dto/users.dto'
import UsersService from 'dataStore/service/users.service'
import { RootState } from 'dataStore/state'
import SetCustomerPasswordDialog from 'components/customers/SetCustomerPasswordDialog'
import CustomersFilters from 'components/customersPage/filters/CustomersFilters'
import {
  DEFAULT_CUSTOMERS_FILTER,
  ICustomersFilterState,
} from 'components/customersPage/filters/customersFilter.types'
import CustomersTable from 'components/customersPage/customersTable/CustomersTable'
import {
  CustomersSortField,
  CustomersSortOrder,
} from 'components/customersPage/customersTable/CustomersTableHead'

/**
 * Страница покупателей: фильтры, серверная сортировка/пагинация, метрики заказов.
 */
const Customers: FC = () => {
  const [customers, setCustomers] = useState<ICustomer[]>([])
  const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | null>(
    null
  )
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [isLoading, setIsLoading] = useState(false)
  const [filters, setFilters] = useState<ICustomersFilterState>(
    DEFAULT_CUSTOMERS_FILTER
  )
  const [order, setOrder] = useState<CustomersSortOrder>('desc')
  const [orderBy, setOrderBy] = useState<CustomersSortField>('id')
  const { pizzerias } = useSelector((state: RootState) => state.pizzerias)
  const tableRef = useRef<HTMLTableElement>(null)

  const pizzeria = pizzerias?.find(
    pizzeriaItem => pizzeriaItem?.id === filters?.pizzeria_id
  )

  /**
   * Применяет фильтры и сбрасывает страницу на первую.
   * @param nextFilters — новое состояние фильтров
   */
  const handleFiltersChange = useCallback(
    (nextFilters: ICustomersFilterState): void => {
      setFilters(nextFilters)
      setPage(0)
    },
    []
  )

  useEffect(() => {
    let isCustomerRequestActive = true

    const loadCustomers = async (): Promise<void> => {
      setIsLoading(true)
      try {
        const response = await UsersService.getAllCustomers({
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          first_name: filters.first_name || undefined,
          last_name: filters.last_name || undefined,
          phone: filters.phone || undefined,
          email: filters.email || undefined,
          city: filters.city || undefined,
          pizzeria_id: filters.pizzeria_id ?? undefined,
          sortBy: orderBy,
          sortOrder: order === 'asc' ? 'ASC' : 'DESC',
        })

        if (!isCustomerRequestActive) {
          return
        }

        setCustomers(Array.isArray(response?.data) ? response.data : [])
        setTotal(typeof response?.total === 'number' ? response.total : 0)
      } catch (error) {
        console.log({ error })
        if (isCustomerRequestActive) {
          setCustomers([])
          setTotal(0)
          toast.error('Не удалось загрузить список клиентов')
        }
      } finally {
        if (isCustomerRequestActive) {
          setIsLoading(false)
        }
      }
    }

    loadCustomers()

    return () => {
      isCustomerRequestActive = false
    }
  }, [filters, page, rowsPerPage, order, orderBy])

  /**
   * Переключает сортировку по колонке.
   * @param _event — клик по заголовку
   * @param property — поле сортировки
   */
  const handleRequestSort = (
    _event: MouseEvent<unknown>,
    property: CustomersSortField
  ): void => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    setPage(0)
  }

  /**
   * Переключает страницу таблицы.
   * @param _event — событие пагинации
   * @param newPage — номер страницы
   */
  const handleChangePage = (_event: unknown, newPage: number): void => {
    setPage(newPage)
  }

  /**
   * Меняет размер страницы.
   * @param changeEvent — событие select
   */
  const handleChangeRowsPerPage = (
    changeEvent: ChangeEvent<HTMLInputElement>
  ): void => {
    setRowsPerPage(parseInt(changeEvent?.target?.value ?? '20', 10))
    setPage(0)
  }

  const { onDownload } = useDownloadExcel({
    currentTableRef: tableRef?.current,
    filename: pizzeria?.address?.city ? pizzeria.address.city : 'Users',
    sheet: 'Users',
  })

  /**
   * Открывает диалог пароля.
   * @param customer — выбранный клиент
   */
  const handleOpenPasswordDialog = (customer: ICustomer): void => {
    setSelectedCustomer(customer)
  }

  const handleClosePasswordDialog = (): void => {
    setSelectedCustomer(null)
  }

  return (
    <main>
      <CustomersFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />
      <Grid container spacing={2} sx={{ mb: '25px' }} alignItems='center'>
        <Grid item xs={12} sm={6} md={2}>
          <Button
            variant='contained'
            fullWidth
            disabled={!customers?.length || isLoading}
            onClick={onDownload}
          >
            Скачать
          </Button>
        </Grid>
        {isLoading ? (
          <Grid item xs={12} sm={6} md={2}>
            <Box display='flex' alignItems='center' gap={1}>
              <CircularProgress size={22} />
              Загрузка…
            </Box>
          </Grid>
        ) : null}
      </Grid>
      <CustomersTable
        customers={customers}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        order={order}
        orderBy={orderBy}
        tableRef={tableRef}
        onRequestSort={handleRequestSort}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        onOpenPasswordDialog={handleOpenPasswordDialog}
      />
      <SetCustomerPasswordDialog
        open={Boolean(selectedCustomer)}
        customer={selectedCustomer}
        onClose={handleClosePasswordDialog}
      />
    </main>
  )
}

export default Customers
